import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { Fragment, useState, VFC } from 'react';
import styled from 'styled-components';

import { TriggerAccountInputModalConfiguration } from 'dashboard/models/trigger';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { Json } from 'lib/types';

const HorizontalScrollableBox = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  font-weight: ${FontWeight.Medium};
  padding: ${Spacing[4]};
  background-color: ${Color.CoreSlate50};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  overflow-x: scroll;
`;

const Row = styled.div`
  display: flex;
  padding: 2px 0;
`;

const ExpandButton = styled.div`
  display: flex;
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
  align-items: center;
  align-self: baseline;
  cursor: pointer;
  &:focus {
    color: ${Color.CorePurple800};
    & rec[fill],
    & path[fill] {
      fill: ${Color.CorePurple800};
    }
  }
`;

const StyledIconCaret = styled(({ expanded, menuItem, ...props }) => <CaretDownIcon {...props} />)<{
  expanded: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0deg' : '-90deg')});
  justify-self: center;
  margin-right: ${Spacing[3]};

  rect[fill],
  path[fill] {
    fill: ${({ theme }) => theme.color.text.primary};
  }
`;

const Key = styled.div`
  white-space: nowrap;
`;

const Value = styled.div`
  white-space: nowrap;
  margin-left: ${Spacing[2]};
  padding-right: ${Spacing[4]}; // Extra space when scrolling to right edge
`;

const Nest = styled.div`
  margin-left: ${Spacing[6]};
`;

interface TriggerAccountInputModalConfigurationPreviewProps {
  inputModalConfiguration: Json;
}

const TriggerAccountInputModalConfigurationPreview: VFC<
  TriggerAccountInputModalConfigurationPreviewProps
> = ({ inputModalConfiguration }) => {
  return (
    <HorizontalScrollableBox role='tree'>
      {isEmpty(inputModalConfiguration) ? (
        'No input fields configured'
      ) : (
        <TriggerAccountInputModalConfigurationPreviewRow
          inputModalConfiguration={inputModalConfiguration}
        />
      )}
    </HorizontalScrollableBox>
  );
};

const TriggerAccountInputModalConfigurationPreviewRow: VFC<{
  inputModalConfiguration: Json;
  depth?: number;
}> = ({ inputModalConfiguration, depth = 0 }) => {
  const [expandedIndexes, updateExpandedIndexes] = useState<number[]>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    depth < 1 ? Object.keys(inputModalConfiguration).map((_key, i) => i) : [],
  );
  const toggleExpansion = (index: number) => {
    updateExpandedIndexes((currentExpandedIndexes) =>
      currentExpandedIndexes.includes(index)
        ? currentExpandedIndexes.filter((i) => i !== index)
        : [...currentExpandedIndexes, index],
    );
  };

  return (
    <>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {Object.entries(inputModalConfiguration).map(([key, value], index) => (
        <Fragment key={`${index}-${depth}`}>
          <Row>
            {typeof value === 'object' && !isEmpty(value) && (
              <ExpandButton
                id={`trigger-account-input-modal-configuration-row__expand-object-${key}-${index}-${depth}`}
                role='button'
                aria-label={
                  expandedIndexes.includes(index) ? 'Hide nested fields' : 'Show nested fields'
                }
                aria-expanded={expandedIndexes.includes(index)}
                tabIndex={0}
                onClick={() => {
                  toggleExpansion(index);
                }}
                onKeyPress={(e) => {
                  if (['Enter', 'Space'].includes(e.nativeEvent.code)) {
                    toggleExpansion(index);
                    e.preventDefault();
                  }
                }}
              >
                <StyledIconCaret expanded={expandedIndexes.includes(index)} />
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <Key>{(value as TriggerAccountInputModalConfiguration)?.key || key}:</Key>
              </ExpandButton>
            )}
            {typeof value !== 'object' && (
              <>
                <Key>{key}:</Key>
                <Value>{String(value) || 'No data'}</Value>
              </>
            )}
          </Row>
          {typeof value === 'object' && !isEmpty(value) && expandedIndexes.includes(index) && (
            <Nest>
              <TriggerAccountInputModalConfigurationPreviewRow
                inputModalConfiguration={value}
                depth={depth + 1}
              />
            </Nest>
          )}
        </Fragment>
      ))}
    </>
  );
};

export default TriggerAccountInputModalConfigurationPreview;
